<script setup lang="ts">
import type { Editor } from "@tiptap/vue-3"

import Document from "@tiptap/extension-document"
import Dropcursor from "@tiptap/extension-dropcursor"
import Paragraph from "@tiptap/extension-paragraph"
import Placeholder from "@tiptap/extension-placeholder"
import Text from "@tiptap/extension-text"
import { EditorContent, useEditor } from "@tiptap/vue-3"

import { FileRef } from "~/classes/agent/FileRef"

const { state: agentState, editor: agentEditor, onKeyDown, onEditorUpdated, isInProgress, cancelAgentTurn } = useAgent()
const { panelAgentInputId } = useGlobals()
const { state: gestureState } = useGestures()
const { currentRootFolder, currentFilePreview } = useNavigation()
const focused = ref(false)
const dragging = computed(() => gestureState.value.gesture === "drag-file")

const editor = useEditor({
  content: agentState.value.inputTurn as any,
  extensions: [
    Document,
    Paragraph,
    Text,
    Placeholder,
    Dropcursor,
    FileRef,
  ],
  editorProps: {
    handleKeyDown: (_, event) => onKeyDown(editor.value!, event),
  },
  onUpdate: ({ editor }) => onEditorUpdated(editor as Editor),
  // We call the update method on create since we want to immediately
  // request any completions
  onCreate: ({ editor }) => onEditorUpdated(editor as Editor),
  onFocus: () => {
    focused.value = true
  },
  onBlur: () => {
    focused.value = false
  },
})

const location = computed(() => {
  if (currentRootFolder.value)
    return currentRootFolder.value
  return currentFilePreview.value
})

const sendEnter = () => {
  if (!editor.value)
    return
  editor.value.commands.enter()
}

onMounted(() => {
  agentEditor.value = editor.value
})
</script>

<template>
  <div
    :id="panelAgentInputId"
    class="panel-agent-input-editor"
    :class="{ focused }"
  >
    <editor-content
      :editor
      class="editor-content"
      :class="{ dragging }"
    />
    <div class="panel-agent-input-chin">
      <div
        v-if="isInProgress"
        class="stop"
        @click="cancelAgentTurn"
      >
        <div>Stop</div>
        <menu-shortcut>■</menu-shortcut>
      </div>
      <div
        v-else
        class="ask"
        @click="sendEnter"
      >
        <div>Send</div>
        <menu-shortcut>↵</menu-shortcut>
      </div>
      <div v-if="location" class="locations">
        <div class="in">
          In
        </div>
        <file-inline :file="location" />
      </div>
    </div>
  </div>
</template>

<style lang="sass">
@import '~/assets/styles/generated/variables.sass'

// Everything must be under this class since we can't use scoped
// styles with tiptap
.panel-agent-input-editor
  @apply relative items-center
  @apply rounded-[18px] outline outline-1.5 outline-[#{$panel-agent-b-input}] overflow-hidden
  @apply text-[#{$global-t-default}] t-panel-base
  @apply backdrop-blur-[#{$menu-blur-default}] backdrop-filter bg-[#{$panel-agent-bg-input}]
  @apply shadow-menu z-overlay-layer
  &.focused
    @apply outline-$global-blue-secondary
  .crystal-ball
    @apply h-5 w-5 flex-none text-[#{$global-stroke-icon-secondary}] mr-[#{$pill-gap-default}]
  .editor-content
    @apply w-full resize-none outline-none overflow-y-visible caret-$global-blue-primary
    &.dragging:hover
      @apply cursor-copy
      @apply bg-$global-steel-accent

  .tiptap
    @apply h-full flex-none px-[#{$panel-agent-px-input}] py-[#{$panel-agent-py-input}]
    @apply outline-none

    p
      line-height: $global-lh-lg
      &.is-editor-empty:first-child::before
        content: 'Chat or drag in files...'
        float: left
        height: 0
        pointer-events: none
        @apply text-[#{$menu-t-input-placeholder}] font-normal
      .suggestion
        @apply bg-$global-steel-accent text-$global-t-secondary
        // TODO: these values were changed from deprecated values
        @apply py-[4px] px-[4px] rounded-[#{$menu-br-default}] t-property-md

  // Needed anymore?
  textarea
    -ms-overflow-style: none
    scrollbar-width: none
  textarea::-webkit-scrollbar
    display: none

.panel-agent-input-chin
  @apply flex items-center justify-between overflow-hidden
  @apply px-[#{$panel-agent-px-input-chin}] py-[#{$panel-agent-py-input-chin}]
  @apply pl-3 // need to align the "ask" with the text input
  @apply bg-[#{$panel-agent-bg-input-chin}]
  @apply gap-2
  .ask, .stop
    @apply inline-flex flex-none items-center gap-0.5
    @apply p-[#{$menu-px-item-shortcut}] -m-[#{$menu-px-item-shortcut}] rounded-sm
    @apply t-panel-xs text-[#{$global-t-tertiary}]
    @apply cursor-pointer
    &:hover
      @apply bg-[#{$menu-bg-item-hover}]
  .locations
    @apply inline-flex items-center t-panel-xs min-w-0 pr-1
  .in
    @apply  text-[#{$global-t-quaternary}]
</style>
